import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calculator, Users, TrendingUp, DollarSign, Calendar, Info, Copy, Link } from 'lucide-react';
import { toast } from 'sonner';

const MONTHLY_SUBSCRIPTION = 199; // MXN
const RAPID_BONUS_PERCENT = 0.25; // 25%
const POOL_PERCENT = 0.42; // 42%

const EarningsSimulator = ({ communityStats = null, theme = 'dark', userType = 'author', user = null }) => {
  const isLight = theme === 'light';
  const isConsumer = userType === 'consumer';
  const referralLink = `https://connect.viubux.com?ref=${user?.username || user?.referral_code || ''}`;
  
  const [inputs, setInputs] = useState({
    myReferrals: 5,
    totalAuthors: communityStats?.total_authors || 10,
    totalSubscribers: communityStats?.total_active || 50,
    months: 12
  });

  const [results, setResults] = useState(null);

  useEffect(() => {
    if (communityStats) {
      setInputs(prev => ({
        ...prev,
        totalAuthors: communityStats.total_authors || prev.totalAuthors,
        totalSubscribers: communityStats.total_active || prev.totalSubscribers
      }));
    }
  }, [communityStats]);

  const calculateEarnings = () => {
    const { myReferrals, totalAuthors, totalSubscribers, months } = inputs;
    
    // Monthly revenue from all subscribers
    const monthlyRevenue = totalSubscribers * MONTHLY_SUBSCRIPTION;
    
    // Pool amount (42% of monthly revenue)
    const monthlyPool = monthlyRevenue * POOL_PERCENT;
    
    // Value per subscriber in pool
    const poolValuePerSubscriber = totalSubscribers > 0 ? monthlyPool / totalSubscribers : 0;
    
    // Author's share of pool (equal division among all authors)
    const authorPoolShare = totalAuthors > 0 ? (1 / totalAuthors) * 100 : 0;
    const authorPoolShareAmount = totalAuthors > 0 ? monthlyPool / totalAuthors : 0;
    
    // Rapid Bonus (25% of each referral's subscription)
    const monthlyRapidBonus = myReferrals * MONTHLY_SUBSCRIPTION * RAPID_BONUS_PERCENT;
    
    // Total monthly earnings
    const monthlyTotal = monthlyRapidBonus + authorPoolShareAmount;
    
    // Projections
    const totalEarnings = monthlyTotal * months;
    const totalRapidBonus = monthlyRapidBonus * months;
    const totalPoolEarnings = authorPoolShareAmount * months;
    
    setResults({
      monthlyRevenue,
      monthlyPool,
      poolValuePerSubscriber,
      authorPoolShare,
      authorPoolShareAmount,
      monthlyRapidBonus,
      monthlyTotal,
      totalEarnings,
      totalRapidBonus,
      totalPoolEarnings,
      months
    });
  };

  useEffect(() => {
    calculateEarnings();
  }, [inputs]);

  const formatCurrency = (amount) => {
    return '$' + Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) + ' MXN';
  };

  return (
    <Card className={isLight ? "bg-white rounded-2xl shadow-sm border border-gray-100" : "card-viubux-dark border-purple-500/30"}>
      <CardHeader>
        <CardTitle className={`gotham-bold text-2xl flex items-center gap-3 ${isLight ? 'text-gray-900' : 'text-white'}`}>
          <Calculator className="w-6 h-6 text-violet-500" />
          Simulador de Ganancias
        </CardTitle>
        <CardDescription className={`verdana-regular ${isLight ? 'text-gray-500' : 'text-white/70'}`}>
          <p className="mb-3">
            Calcula tu potencial de ingresos basado en el crecimiento de <strong>tu comunidad</strong>. 
            Entre más compartas tu enlace de referido y más lo compartas con tus familiares y amigos, ¡mayor será tu ingreso mensual!
          </p>
          
          {/* Enlace de referido destacado */}
          {user && (
            <div className={`flex items-center gap-2 p-3 rounded-lg mb-3 ${isLight ? 'bg-violet-50 border border-violet-200' : 'bg-violet-500/10 border border-violet-500/30'}`}>
              <Link className="w-4 h-4 text-violet-500 flex-shrink-0" />
              <span className={`text-xs font-mono truncate flex-1 ${isLight ? 'text-violet-700' : 'text-violet-300'}`}>
                {referralLink}
              </span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(referralLink);
                  toast.success('¡Enlace copiado!');
                }}
                className={`p-1.5 rounded-lg transition-colors ${isLight ? 'hover:bg-violet-100' : 'hover:bg-violet-500/20'}`}
              >
                <Copy className="w-4 h-4 text-violet-500" />
              </button>
            </div>
          )}
          
          <p className={`text-sm ${isLight ? 'text-green-700 bg-green-50 border border-green-200' : 'text-green-300 bg-green-500/10 border border-green-500/30'} p-3 rounded-lg mb-3`}>
            💡 Cuando superes el costo de tu suscripción mensual, <strong>tu membresía se paga sola</strong> y has activado un modelo económico autosustentable mientras disfrutas de los títulos de la colección de ViubuxConnect en Viubux.com
          </p>
          
          <p>
            Calcula cuánto te gustaría generar mensualmente basado en la cantidad de usuarios que debes prospectar. 
            <strong className="text-violet-500"> ¡Entre más corras la voz, más ganas!</strong>
          </p>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Input Controls */}
        <div className={`grid ${isConsumer ? 'md:grid-cols-2' : 'md:grid-cols-4'} gap-4 mb-8`}>
          <div>
            <label className={`block text-sm mb-2 flex items-center gap-2 ${isLight ? 'text-gray-600' : 'text-white/70'}`}>
              <Users className="w-4 h-4" /> Mis Referidos
            </label>
            <input
              type="number"
              min="0"
              value={inputs.myReferrals}
              onChange={(e) => setInputs({...inputs, myReferrals: parseInt(e.target.value) || 0})}
              className={`w-full rounded-xl px-4 py-3 text-center text-xl font-bold focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                isLight 
                  ? 'bg-violet-50 border border-violet-200 text-gray-900' 
                  : 'bg-black/30 border border-purple-500/30 text-white'
              }`}
            />
            <p className={`text-xs mt-1 text-center ${isLight ? 'text-gray-400' : 'text-white/40'}`}>Suscriptores que refiero</p>
          </div>
          
          {!isConsumer && (
            <>
              <div>
                <label className={`block text-sm mb-2 flex items-center gap-2 ${isLight ? 'text-gray-600' : 'text-white/70'}`}>
                  <TrendingUp className="w-4 h-4" /> Total Autores
                </label>
                <input
                  type="number"
                  min="1"
                  value={inputs.totalAuthors}
                  onChange={(e) => setInputs({...inputs, totalAuthors: parseInt(e.target.value) || 1})}
                  className={`w-full rounded-xl px-4 py-3 text-center text-xl font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isLight 
                      ? 'bg-blue-50 border border-blue-200 text-gray-900' 
                      : 'bg-black/30 border border-blue-500/30 text-white'
                  }`}
                />
                <p className={`text-xs mt-1 text-center ${isLight ? 'text-gray-400' : 'text-white/40'}`}>En el sello</p>
              </div>
              
              <div>
                <label className={`block text-sm mb-2 flex items-center gap-2 ${isLight ? 'text-gray-600' : 'text-white/70'}`}>
                  <DollarSign className="w-4 h-4" /> Total Suscriptores
                </label>
                <input
                  type="number"
                  min="0"
                  value={inputs.totalSubscribers}
                  onChange={(e) => setInputs({...inputs, totalSubscribers: parseInt(e.target.value) || 0})}
                  className={`w-full rounded-xl px-4 py-3 text-center text-xl font-bold focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    isLight 
                      ? 'bg-green-50 border border-green-200 text-gray-900' 
                      : 'bg-black/30 border border-green-500/30 text-white'
                  }`}
                />
                <p className={`text-xs mt-1 text-center ${isLight ? 'text-gray-400' : 'text-white/40'}`}>Activos en la red</p>
              </div>
            </>
          )}
          
          <div>
            <label className={`block text-sm mb-2 flex items-center gap-2 ${isLight ? 'text-gray-600' : 'text-white/70'}`}>
              <Calendar className="w-4 h-4" /> Meses
            </label>
            <input
              type="number"
              min="1"
              max="60"
              value={inputs.months}
              onChange={(e) => setInputs({...inputs, months: parseInt(e.target.value) || 1})}
              className={`w-full rounded-xl px-4 py-3 text-center text-xl font-bold focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                isLight 
                  ? 'bg-amber-50 border border-amber-200 text-gray-900' 
                  : 'bg-black/30 border border-yellow-500/30 text-white'
              }`}
            />
            <p className={`text-xs mt-1 text-center ${isLight ? 'text-gray-400' : 'text-white/40'}`}>Proyección</p>
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-6">
            {/* Monthly Breakdown */}
            <div className={`p-6 rounded-xl ${isLight ? 'bg-violet-50 border border-violet-100' : 'bg-gradient-to-r from-purple-500/10 to-transparent border border-purple-500/20'}`}>
              <h4 className={`gotham-bold text-lg mb-4 ${isLight ? 'text-gray-900' : 'text-white'}`}>📊 Desglose Mensual</h4>
              <div className={`grid ${isConsumer ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
                <div className={`p-4 rounded-xl ${isLight ? 'bg-white border border-gray-100' : 'bg-black/20'}`}>
                  <p className="text-green-500 text-sm mb-1">Bono Rápido (25%)</p>
                  <p className={`gotham-bold text-2xl ${isLight ? 'text-gray-900' : 'text-white'}`}>{formatCurrency(results.monthlyRapidBonus)}</p>
                  <p className={`text-xs ${isLight ? 'text-gray-400' : 'text-white/40'}`}>{inputs.myReferrals} referidos × $49.75</p>
                </div>
                {!isConsumer && (
                  <div className={`p-4 rounded-xl ${isLight ? 'bg-white border border-gray-100' : 'bg-black/20'}`}>
                    <p className="text-violet-500 text-sm mb-1">Mi Parte del Pool (42%)</p>
                    <p className={`gotham-bold text-2xl ${isLight ? 'text-gray-900' : 'text-white'}`}>{formatCurrency(results.authorPoolShareAmount)}</p>
                    <p className={`text-xs ${isLight ? 'text-gray-400' : 'text-white/40'}`}>{results.authorPoolShare.toFixed(1)}% del pool mensual</p>
                  </div>
                )}
                <div className={`p-4 rounded-xl border-2 ${isLight ? 'bg-amber-50 border-amber-300' : 'bg-black/20 border-yellow-500/50'}`}>
                  <p className={`text-sm mb-1 ${isLight ? 'text-amber-600' : 'text-yellow-400'}`}>Total Mensual</p>
                  <p className={`gotham-bold text-3xl ${isLight ? 'text-amber-600' : 'text-yellow-400'}`}>
                    {formatCurrency(isConsumer ? results.monthlyRapidBonus : results.monthlyTotal)}
                  </p>
                  <p className={`text-xs ${isLight ? 'text-gray-400' : 'text-white/40'}`}>Ingreso recurrente</p>
                </div>
              </div>
            </div>

            {/* Projection */}
            <div className={`p-6 rounded-xl ${isLight ? 'bg-green-50 border border-green-100' : 'bg-gradient-to-r from-green-500/10 to-transparent border border-green-500/20'}`}>
              <h4 className={`gotham-bold text-lg mb-4 ${isLight ? 'text-gray-900' : 'text-white'}`}>🚀 Proyección a {results.months} Meses</h4>
              <div className={`grid ${isConsumer ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
                <div className={`p-4 rounded-xl ${isLight ? 'bg-white border border-gray-100' : 'bg-black/20'}`}>
                  <p className="text-green-500 text-sm mb-1">Total Bono Rápido</p>
                  <p className={`gotham-bold text-2xl ${isLight ? 'text-gray-900' : 'text-white'}`}>{formatCurrency(results.totalRapidBonus)}</p>
                </div>
                {!isConsumer && (
                  <div className={`p-4 rounded-xl ${isLight ? 'bg-white border border-gray-100' : 'bg-black/20'}`}>
                    <p className="text-violet-500 text-sm mb-1">Total del Pool</p>
                    <p className={`gotham-bold text-2xl ${isLight ? 'text-gray-900' : 'text-white'}`}>{formatCurrency(results.totalPoolEarnings)}</p>
                  </div>
                )}
                <div className={`p-4 rounded-xl border-2 ${isLight ? 'bg-green-100 border-green-400' : 'bg-black/20 border-green-500/50'}`}>
                  <p className="text-green-500 text-sm mb-1">GANANCIA TOTAL</p>
                  <p className="gotham-bold text-4xl text-green-500">
                    {formatCurrency(isConsumer ? results.totalRapidBonus : results.totalEarnings)}
                  </p>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className={`p-4 rounded-xl flex items-start gap-3 ${isLight ? 'bg-gray-50' : 'bg-black/20'}`}>
              <Info className="w-5 h-5 text-violet-500 mt-0.5 flex-shrink-0" />
              <div className={`text-sm verdana-regular ${isLight ? 'text-gray-600' : 'text-white/60'}`}>
                <p>
                  {isConsumer 
                    ? 'Como Audio-Lector, ganas el 25% de cada suscripción que refieras. Tus ganancias son recurrentes mientras tus referidos mantengan su suscripción activa.'
                    : <>
                        <strong className={isLight ? 'text-gray-900' : 'text-white'}>Valor actual de la "Acción" del Pool:</strong>{' '}
                        <span className="text-violet-500">{formatCurrency(results.poolValuePerSubscriber)}</span> por suscriptor activo.
                        El Pool de Autores (42%) se distribuye equitativamente entre todos los autores activos.
                      </>
                  }
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EarningsSimulator;
